/**
 * NotificationBell Component
 *
 * Bell icon button with unread count badge and dropdown panel.
 * Main notification entry point in the application header.
 *
 * Features:
 * - Bell icon with unread count badge
 * - Dropdown panel on click
 * - Real-time notification updates via WebSocket
 * - Connection status indicator
 * - Click outside to close
 * - Keyboard accessibility (ESC to close)
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @example
 * <NotificationBell />
 */

import React, { useState, useEffect, useRef } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { useNotificationSocket } from '../../hooks/notifications/useNotificationSocket';
import NotificationDropdown from './NotificationDropdown';

/**
 * NotificationBell Component
 *
 * Uses WebSocket connection for real-time notifications.
 * Displays unread count badge and dropdown panel.
 */
export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // WebSocket connection for real-time notifications
  const {
    connected,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotificationSocket();

  // ============================================================================
  // KEYBOARD ACCESSIBILITY
  // ============================================================================

  useEffect(() => {
    /**
     * Handle ESC key to close dropdown
     */
    const handleEscape = (  _e) => {
      if (_e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Toggle dropdown open/closed
   */
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Close dropdown
   */
  const closeDropdown = () => {
    setIsOpen(false);
  };

  /**
   * Mark notification as read and close dropdown if navigating
   */
  const handleMarkAsRead = (notificationId) => {
    markAsRead(notificationId);
    // Keep dropdown open after marking as read
  };

  /**
   * Mark all notifications as read
   */
  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  /**
   * Delete notification
   */
  const handleDelete = (notificationId) => {
    deleteNotification(notificationId);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        type="button"
        onClick={toggleDropdown}
        className={`relative flex items-center justify-center w-10 h-10 rounded-lg border-none transition-colors ${
          isOpen ? 'bg-gray-100' : 'bg-transparent'
        }`}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Bell icon - 20px size per mockup spec */}
        <BellIcon className="h-5 w-5 text-gray-700" />

        {/* Unread indicator - 8px red dot at top: 6px, right: 6px per mockup spec */}
        {unreadCount > 0 && (
          <span
            className="absolute w-2 h-2 bg-red-600 rounded-full border-2 border-white"
            style={{ top: '6px', right: '6px' }}
            aria-hidden="true"
          />
        )}

        {/* Connection status indicator (yellow dot if disconnected) */}
        {!connected && (
          <span
            className="absolute bottom-0 right-0 w-2 h-2 bg-yellow-500 rounded-full border border-white"
            title="Reconnecting to notification server..."
          />
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <NotificationDropdown
          notifications={notifications}
          onClose={closeDropdown}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onDelete={handleDelete}
          loading={false}
        />
      )}
    </div>
  );
}
